import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Monitor, Tablet, Smartphone } from "lucide-react";

const devices = [
  { key: "desktop", label: "Desktop", icon: Monitor, width: "100%", height: 140 },
  { key: "tablet", label: "Tablet", icon: Tablet, width: "70%", height: 160 },
  { key: "mobile", label: "Mobile", icon: Smartphone, width: "40%", height: 180 },
] as const;

export function ResponsivePreview() {
  const [active, setActive] = useState(0);
  const device = devices[active];

  return (
    <div className="space-y-3">
      <div className="bg-card rounded-xl p-4 shadow-[0_1px_2px_rgba(10,13,20,0.03),0_0_0_1px_rgba(23,23,23,0.04)]">
        {/* Header with device tabs */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
            <Monitor className="h-3 w-3" />
            Responsive Preview
          </p>
          <div className="flex bg-secondary rounded-lg p-0.5">
            {devices.map((d, i) => (
              <button
                key={d.key}
                onClick={() => setActive(i)}
                className={`relative flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                  active === i ? "text-foreground" : "text-muted-foreground hover:text-foreground/70"
                }`}
              >
                {active === i && (
                  <motion.div
                    className="absolute inset-0 bg-card rounded-md shadow-sm"
                    layoutId="device-tab"
                    transition={{ type: "spring", stiffness: 400, damping: 28 }}
                  />
                )}
                <span className="relative z-10 flex items-center gap-1.5">
                  <d.icon className="h-3 w-3" strokeWidth={1.5} />
                  {d.label}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Animated preview frame */}
        <div className="flex justify-center">
          <motion.div
            className="bg-secondary rounded-xl border border-border overflow-hidden"
            animate={{ width: device.width, height: device.height }}
            transition={{ type: "spring", stiffness: 300, damping: 28 }}
            style={{ minWidth: 120 }}
          >
            {/* Mini site mockup */}
            <AnimatePresence mode="wait">
              <motion.div
                key={device.key}
                className="h-full flex flex-col"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.2 }}
              >
                {/* Nav bar */}
                <div className="flex items-center justify-between px-3 py-2 border-b border-border/50">
                  <div className="w-4 h-4 rounded bg-primary/20" />
                  <div className="flex gap-1.5">
                    {(active === 0 ? [1, 2, 3, 4] : active === 1 ? [1, 2, 3] : [1]).map((n) => (
                      <div key={n} className="h-1.5 w-6 rounded-full bg-border" />
                    ))}
                  </div>
                  <div className="h-4 w-10 rounded bg-primary/15" />
                </div>

                {/* Hero area */}
                <div className={`flex-1 p-3 flex flex-col ${active === 2 ? "items-center text-center" : ""}`}>
                  <div className="space-y-1.5 mb-3">
                    <div className="h-2 rounded-full bg-primary/20 w-3/5" />
                    <div className="h-1.5 rounded-full bg-border w-full" />
                    <div className="h-1.5 rounded-full bg-border w-4/5" />
                  </div>
                  <div className={`flex gap-1.5 ${active === 2 ? "justify-center" : ""} mb-3`}>
                    <div className="h-5 w-14 rounded-md bg-primary/20" />
                    <div className="h-5 w-14 rounded-md bg-border" />
                  </div>

                  {/* Content cards */}
                  {active === 0 && (
                    <div className="flex gap-1.5 mt-auto">
                      {[1, 2, 3].map((n) => (
                        <div key={n} className="flex-1 h-10 rounded-md bg-card border border-border/50" />
                      ))}
                    </div>
                  )}
                  {active === 1 && (
                    <div className="flex gap-1.5 mt-auto">
                      {[1, 2].map((n) => (
                        <div key={n} className="flex-1 h-12 rounded-md bg-card border border-border/50" />
                      ))}
                    </div>
                  )}
                  {active === 2 && (
                    <div className="mt-auto space-y-1.5">
                      <div className="h-14 rounded-md bg-card border border-border/50" />
                    </div>
                  )}
                </div>
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
